import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExploreIcon from '@material-ui/icons/Explore';
import FavoriteIcon from '@material-ui/icons/Favorite';
import DateRangeIcon from '@material-ui/icons/DateRange';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { Link } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  heroWrap: {
    paddingTop: theme.spacing(6),
  },
  hero: {
    borderRadius: 34,
    overflow: 'hidden',
    background: 'linear-gradient(130deg, #1f2b24 0%, #3c352f 46%, #6f5a4c 110%)',
    boxShadow: '0 30px 60px rgba(40, 27, 16, 0.25)',
    border: '1px solid rgba(255, 245, 236, 0.2)',
  },
  heroGrid: {
    minHeight: 420,
  },
  heroLeft: {
    padding: theme.spacing(6, 5),
    color: '#fff6eb',
  },
  badge: {
    backgroundColor: 'rgba(255, 241, 224, 0.16)',
    border: '1px solid rgba(255, 241, 224, 0.32)',
    color: '#fff4e8',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  heroTitle: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    fontSize: 'clamp(2rem, 4.6vw, 3.5rem)',
    lineHeight: 1.05,
    letterSpacing: '-0.03em',
    maxWidth: 620,
  },
  heroSub: {
    marginTop: theme.spacing(2),
    maxWidth: 560,
    color: 'rgba(255, 244, 232, 0.9)',
    fontSize: '1.18rem',
  },
  heroCta: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  outlineBtn: {
    color: '#fff3e3',
    borderColor: 'rgba(255, 243, 227, 0.8)',
  },
  statCard: {
    margin: theme.spacing(6, 4, 6, 0),
    borderRadius: 22,
    background: 'linear-gradient(180deg, rgba(255, 252, 247, 0.98) 0%, rgba(246, 233, 220, 0.96) 100%)',
    border: '1px solid rgba(179, 146, 116, 0.3)',
    padding: theme.spacing(3),
  },
  statBig: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    fontSize: '2rem',
    lineHeight: 1.05,
  },
  section: {
    marginTop: theme.spacing(6),
  },
  sectionTitle: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  featureCard: {
    height: '100%',
    borderRadius: 18,
    border: '1px solid rgba(174, 149, 124, 0.26)',
    background: 'linear-gradient(180deg, #fffdf8 0%, #fff7ee 100%)',
  },
  featureIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'grid',
    placeItems: 'center',
    background: 'linear-gradient(140deg, #f6ddca 0%, #edc4a2 100%)',
    marginBottom: theme.spacing(1.5),
  },
  collectionCard: {
    borderRadius: 18,
    color: '#fff',
    minHeight: 260,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    padding: theme.spacing(2.5),
    position: 'relative',
    overflow: 'hidden',
    border: '1px solid rgba(255, 245, 235, 0.24)',
    transition: 'transform 220ms ease, box-shadow 220ms ease',
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 18px 30px rgba(31, 22, 16, 0.24)',
    },
  },
  collectionOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.6) 100%)',
  },
  collectionContent: {
    position: 'relative',
    zIndex: 2,
  },
  collectionChipRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
  },
  collectionChip: {
    background: 'rgba(255, 249, 242, 0.18)',
    color: '#fff',
    border: '1px solid rgba(255, 249, 242, 0.28)',
    fontWeight: 700,
  },
  collectionAction: {
    marginTop: theme.spacing(1.5),
    borderColor: 'rgba(255, 247, 237, 0.75)',
    color: '#fff',
  },
  footerCta: {
    marginTop: theme.spacing(6),
    borderRadius: 24,
    padding: theme.spacing(4),
    border: '1px solid rgba(169, 144, 124, 0.34)',
    background: 'linear-gradient(125deg, #fff8ef 0%, #f4ebdf 55%, #efe4d8 100%)',
  },
  footerLeftTitle: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  footerStat: {
    borderRadius: 14,
    background: 'rgba(255, 255, 255, 0.58)',
    border: '1px solid rgba(161, 136, 110, 0.25)',
    padding: theme.spacing(1.2, 1.5),
    minWidth: 118,
  },
  footerActions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
      marginTop: theme.spacing(2),
    },
  },
}));

const features = [
  { icon: <ExploreIcon />, title: 'Map-first Discovery', text: 'Explore restaurants, hotels, and attractions on a live map with useful context.' },
  { icon: <CompareArrowsIcon />, title: 'Smart Compare', text: 'Compare top options side-by-side to choose faster and avoid decision fatigue.' },
  { icon: <FavoriteIcon />, title: 'Saved Collections', text: 'Shortlist places as you browse and keep all top picks in one clean workspace.' },
  { icon: <DateRangeIcon />, title: 'Itinerary Builder', text: 'Plan day-by-day routes and share a clean URL with friends or collaborators.' },
];

const collections = [
  {
    title: 'City Food Trails',
    places: '128 places',
    image: 'linear-gradient(140deg, #6f4b3f 0%, #8a6958 60%, #b88f75 130%)',
    tags: ['Street Eats', 'Chef Picks'],
  },
  {
    title: 'Weekend Nature Spots',
    places: '84 places',
    image: 'linear-gradient(140deg, #2f4737 0%, #486b57 56%, #6c8f7d 125%)',
    tags: ['Sunrise', 'Hiking'],
  },
  {
    title: 'Boutique Stays',
    places: '62 places',
    image: 'linear-gradient(140deg, #3a3844 0%, #5a5569 58%, #7f778b 130%)',
    tags: ['Design', 'Luxury'],
  },
];

const HomePage = () => {
  const classes = useStyles();

  return (
    <Container maxWidth="xl" className={classes.heroWrap}>
      <section className={classes.hero}>
        <Grid container className={classes.heroGrid}>
          <Grid item xs={12} md={8} className={classes.heroLeft}>
            <Chip label="Your Travel Guide" className={classes.badge} />
            <Typography className={classes.heroTitle}>
              Plan better journeys with a calm, modern travel workspace.
            </Typography>
            <Typography className={classes.heroSub}>
              Discover places, compare them quickly, save favorites, and build itineraries with clarity from the first click.
            </Typography>
            <Box className={classes.heroCta}>
              <Button component={Link} to="/explore" color="secondary" variant="contained" size="large">
                Start Exploring
              </Button>
              <Button component={Link} to="/saved" variant="outlined" size="large" className={classes.outlineBtn}>
                View Saved Places
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} className={classes.statCard}>
              <Typography color="textSecondary" variant="subtitle2">This week highlights</Typography>
              <Box mt={2}>
                <Typography className={classes.statBig}>2,480+</Typography>
                <Typography color="textSecondary">places explored by active planners</Typography>
              </Box>
              <Box mt={2}>
                <Typography className={classes.statBig}>420+</Typography>
                <Typography color="textSecondary">itineraries generated and shared</Typography>
              </Box>
              <Box mt={2} display="flex" alignItems="center" gridGap={6}>
                <LocationOnIcon color="secondary" fontSize="small" />
                <Typography variant="body2" color="textSecondary">Popular right now: Tokyo, Dubai, Delhi, Lisbon</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </section>

      <section className={classes.section}>
        <Typography variant="h4" className={classes.sectionTitle} gutterBottom>
          Everything you need in one place
        </Typography>
        <Grid container spacing={3}>
          {features.map((feature) => (
            <Grid item xs={12} sm={6} md={3} key={feature.title}>
              <Card elevation={0} className={classes.featureCard}>
                <CardContent>
                  <div className={classes.featureIconWrap}>{feature.icon}</div>
                  <Typography variant="h6">{feature.title}</Typography>
                  <Typography color="textSecondary" style={{ marginTop: 8 }}>
                    {feature.text}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </section>

      <section className={classes.section}>
        <Typography variant="h4" className={classes.sectionTitle} gutterBottom>
          Curated collections
        </Typography>
        <Grid container spacing={3}>
          {collections.map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <Card elevation={0} className={classes.collectionCard} style={{ background: item.image }}>
                <div className={classes.collectionOverlay} />
                <div className={classes.collectionContent}>
                  <Typography variant="h5">{item.title}</Typography>
                  <Typography variant="body2" style={{ opacity: 0.9 }}>{item.places}</Typography>
                  <Box className={classes.collectionChipRow}>
                    {item.tags.map((tag) => (
                      <Chip key={`${item.title}-${tag}`} label={tag} size="small" className={classes.collectionChip} />
                    ))}
                  </Box>
                  <Button
                    component={Link}
                    to="/explore"
                    variant="outlined"
                    size="small"
                    className={classes.collectionAction}
                  >
                    Explore Collection
                  </Button>
                </div>
              </Card>
            </Grid>
          ))}
        </Grid>
      </section>

      <Paper elevation={0} className={classes.footerCta}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="h4" className={classes.footerLeftTitle}>Ready to build your next plan?</Typography>
            <Typography color="textSecondary" style={{ marginTop: 8, maxWidth: 560 }}>
              Start with Explore, shortlist favorites, compare options, then turn your picks into a clean shareable itinerary.
            </Typography>
            <Box mt={2} display="flex" gridGap={10} flexWrap="wrap">
              <Box className={classes.footerStat}>
                <Typography variant="subtitle2">Avg planning time</Typography>
                <Typography variant="h6">12 mins</Typography>
              </Box>
              <Box className={classes.footerStat}>
                <Typography variant="subtitle2">Collections</Typography>
                <Typography variant="h6">270+</Typography>
              </Box>
              <Box className={classes.footerStat}>
                <Typography variant="subtitle2">Saved places</Typography>
                <Typography variant="h6">50k+</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box className={classes.footerActions}>
              <Button component={Link} to="/explore" color="primary" variant="contained">
                Open Explore
              </Button>
              <Button component={Link} to="/about" color="primary" variant="outlined">
                Learn More
              </Button>
              <Button component={Link} to="/faq" color="primary">
                FAQ
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default HomePage;
